import LabeledSelect from 'components/LabeledSelect';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { useEffect, useState } from 'react';
import {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { buildDevicesBySystemAndTypeMap } from '../../../utils/builder.utils';
import CreateRadialAlert, { VerifyRadialAlert } from './components/CreateRadialAlert';
import ToggleFlashingLights from './components/ToggleFlashingLights';

const normalizeTypeKey = (s) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
const BUOY_SYSTEM_LABEL = 'Buoy';
const BUOY_SYSTEM_TYPE = 'RescAlert Devices';
const BUOY_DEVICE_TYPE = 'Buoy Conditions';

const isBuoyActionType = (deviceType = '') =>
  normalizeTypeKey(deviceType) === normalizeTypeKey(`${BUOY_SYSTEM_TYPE} ${BUOY_DEVICE_TYPE}`);

const toValueOptions = (values = []) => values.map((value) => ({ value, label: value }));

const DeviceAction = ({ isGroup = false, setData, data, setError, readOnly = false }) => {
  const { data: connectedDevices = [], isLoading: loadingConnectedDevices } =
    useGetConnectedDevicesQuery();
  const { data: deviceGroups = [] } = useGetGroupsQuery();
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const devicesBySystemAndTypeMap =
    connectedDevices.length > 0 ? buildDevicesBySystemAndTypeMap(connectedDevices) : new Map();
  const [systemType, setSystemType] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [group, setGroup] = useState();

  useEffect(() => {
    if (
      (data?.node_id === undefined && data?.group_id === undefined) ||
      data?.deviceType === undefined ||
      data?.selectedDevice === undefined ||
      data?.selectedFunction === undefined
    ) {
      setData({
        group_id: '',
        node_id: '',
        deviceType: '',
        selectedDevice: '',
        selectedFunction: '',
        error: true,
      });
    }

    if (data?.group_id) {
      const foundGroup = deviceGroups.find((g) => g.id === data.group_id);
      setGroup(foundGroup ?? null);
    }

    if (data?.deviceType) {
      const selectedDeviceType = data.selectedDevice?.device_type || '';
      const selectedSystemType = data.selectedDevice?.system_type || '';

      if (isBuoyActionType(data.deviceType)) {
        setSystemType(BUOY_SYSTEM_LABEL);
        setDeviceType(BUOY_DEVICE_TYPE);
      } else {
        setDeviceType(selectedDeviceType);
        setSystemType(selectedSystemType);
      }
    }
  }, [deviceGroups]);

  const updateData = (item) => {
    let newObj = { ...data, ...item };
    // Trim keys to what the active function needs + universal ones
    newObj = Object.keys(newObj).reduce((acc, key) => {
      if (
        deviceFunctions[newObj.selectedFunction]?.keys.includes(key) ||
        universalKeys.includes(key)
      )
        acc[key] = newObj[key];
      return acc;
    }, {});

    setData({
      ...newObj,
      error: verifyData(newObj),
    });
  };

  const verifyData = (obj) => {
    let error =
      (obj.node_id === '' && obj.group_id === '') ||
      obj.deviceType === '' ||
      obj.selectedFunction === '';

    // Verify function-specific payload
    error = deviceFunctions[obj.selectedFunction]?.verify
      ? !deviceFunctions[obj.selectedFunction].verify(obj)
      : true;

    if (!error) setError(null);
    return error;
  };

  const functionList = {
    'device group': ['Create Radial Alert', 'Toggle Flashing Lights', 'Toggle Siren/Giant Voice'],
    'flood stage': ['Create Radial Alert'],
    'rescalert devices manual road closure': ['Toggle Manual Road Closure'],
    'rescalert devices technology bridge_flashing lights': ['Toggle Flashing Lights'],
    'rescalert devices technology bridge_siren/giant voice': ['Toggle Siren/Giant Voice'],
    'technology bridge_flashing lights': ['Toggle Flashing Lights'],
    'technology bridge_siren/giant voice': ['Toggle Siren/Giant Voice'],
    'usgs monitoring station': ['Create Radial Alert'],
    'rescalert devices buoy conditions': ['Create Radial Alert'],
  };

  const deviceFunctions = {
    'Create Radial Alert': {
      component: (
        <CreateRadialAlert
          dataObj={data}
          deviceList={
            isGroup && group && Array.isArray(group.devices)
              ? connectedDevices.filter((d) => group.devices.includes(d.id))
              : connectedDevices
          }
          onUpdate={updateData}
          readOnly={readOnly}
        />
      ),
      verify: (obj) => {
        const verified = VerifyRadialAlert(obj);
        if (!verified) setError({ message: 'Action incomplete, check for missing fields.' });
        return verified;
      },
      keys: [
        'headline',
        'description',
        'type',
        'radius',
        'severity',
        'city',
        'state',
        'isInternalUse',
      ],
    },
    'Toggle Manual Road Closure': {
      component: (
        <ToggleFlashingLights
          dataObj={data}
          deviceList={connectedDevices}
          onUpdate={updateData}
          group={group}
          readOnly={readOnly}
        />
      ),
      verify: (obj) => {
        if (obj.status === null || obj.status === undefined || (!obj.node_id && !obj.group_id)) {
          setError({ message: 'Action incomplete, check for missing fields.' });
          return false;
        }
        return true;
      },
      keys: ['status', 'comment'],
    },
    'Toggle Flashing Lights': {
      component: (
        <ToggleFlashingLights
          dataObj={data}
          deviceList={connectedDevices}
          onUpdate={updateData}
          group={group}
          readOnly={readOnly}
        />
      ),
      verify: (obj) => {
        if (obj.status === null || obj.status === undefined || (!obj.node_id && !obj.group_id)) {
          setError({ message: 'Action incomplete, check for missing fields.' });
          return false;
        }
        return true;
      },
      keys: ['status', 'comment'],
    },
    'Toggle Siren/Giant Voice': {
      component: (
        <ToggleFlashingLights
          dataObj={data}
          deviceList={connectedDevices}
          onUpdate={updateData}
          group={group}
          readOnly={readOnly}
        />
      ),
      verify: (obj) => {
        if (obj.status === null || obj.status === undefined || (!obj.node_id && !obj.group_id)) {
          setError({ message: 'Action incomplete, check for missing fields.' });
          return false;
        }
        return true;
      },
      keys: ['status', 'comment', 'interval'],
    },
  };

  const universalKeys = ['node_id', 'group_id', 'deviceType', 'selectedDevice', 'selectedFunction'];

  /**
   * Derives the available function list for a group by inspecting each device in
   * group.devices, matching its system_type + device_type against functionList,
   * and returning the deduplicated union of all matched functions.
   */
  const getGroupFunctions = (grp) => {
    if (!grp?.devices?.length) return [];
    const functionSet = new Set();
    for (const deviceId of grp.devices) {
      const device = connectedDevices.find((d) => d.id === deviceId);
      if (!device) continue;
      const typeKey = normalizeTypeKey(`${device.system_type} ${device.device_type}`);
      const funcs = functionList[typeKey] || [];
      funcs.forEach((f) => functionSet.add(f));
    }
    return [...functionSet];
  };

  const Toolbar = ({ children }) => (
    <div className="global-action-builder-row d-flex flex-column flex-md-row p-3 rounded-2 m-2 gap-3 align-items-stretch align-items-md-center">
      {children}
    </div>
  );

  if (loadingConnectedDevices) return <WidgetLoadingAnimation />;

  if (!isGroup) {
    const hasSystem =
      systemType === BUOY_SYSTEM_LABEL ? true : !!systemType && !!deviceTypes[systemType];

    const normalizedTypeKey = normalizeTypeKey(data?.deviceType);

    const systemLocked =
      readOnly && !!data.deviceType && !!systemType && !devicesBySystemAndTypeMap?.has(systemType);
    const deviceTypeLocked =
      readOnly &&
      !!data.deviceType &&
      !!deviceType &&
      !devicesBySystemAndTypeMap?.get(systemType)?.has(deviceType);

    const hasBuoyDevice = connectedDevices.some(
      (device) =>
        normalizeTypeKey(device.system_type) === normalizeTypeKey(BUOY_SYSTEM_TYPE) &&
        normalizeTypeKey(device.device_type) === normalizeTypeKey(BUOY_DEVICE_TYPE),
    );

    const systemOptions = [
      ...(hasBuoyDevice ? [{ value: BUOY_SYSTEM_LABEL, label: BUOY_SYSTEM_LABEL }] : []),
      ...Object.keys(deviceTypes)
        .filter((key) => key.toLowerCase() !== 'exacq vision')
        .filter((key) => devicesBySystemAndTypeMap?.has(key))
        .map((type) => ({ value: type, label: type })),
    ];

    const deviceTypeOptions = !hasSystem
      ? []
      : systemType === BUOY_SYSTEM_LABEL
        ? [{ value: BUOY_DEVICE_TYPE, label: BUOY_DEVICE_TYPE }]
        : Object.keys(deviceTypes[systemType])
            .filter((key) => key.toLowerCase() !== 'overwatch vms')
            .filter(
              (key) =>
                !(
                  systemType === BUOY_SYSTEM_TYPE &&
                  normalizeTypeKey(key) === normalizeTypeKey(BUOY_DEVICE_TYPE)
                ),
            )
            .filter(
              (key) => (devicesBySystemAndTypeMap?.get(systemType)?.get(key) || []).length > 0,
            )
            .map((type) => ({ value: type, label: type.replace('_', ' ') }));

    return (
      <div className="d-flex flex-column w-100">
        <Toolbar>
          <LabeledSelect
            label="System:"
            locked={systemLocked}
            disabled={readOnly}
            value={systemType || ''}
            onChange={(nextSystem) => {
              setSystemType(nextSystem);
              setDeviceType('');

              updateData({
                node_id: '',
                deviceType: '',
                selectedDevice: '',
                selectedFunction: '',
                error: true,
              });
            }}
            options={systemOptions}
            className="flex-fill"
          />

          {hasSystem && (
            <LabeledSelect
              label="Device Type:"
              locked={deviceTypeLocked}
              disabled={readOnly}
              value={deviceType || ''}
              onChange={(nextType) => {
                setDeviceType(nextType);

                if (systemType === BUOY_SYSTEM_LABEL) {
                  updateData({
                    deviceType: `${BUOY_SYSTEM_TYPE} ${BUOY_DEVICE_TYPE}`,
                    node_id: '',
                    selectedDevice: '',
                    selectedFunction: '',
                    error: true,
                  });
                  return;
                }

                const sys = deviceTypes[systemType];
                const node = sys?.[nextType];

                if (
                  node &&
                  (Object.keys(node).length === 0 || Object.keys(node).includes('validDevices'))
                ) {
                  updateData({
                    deviceType: `${systemType} ${nextType}`,
                    node_id: '',
                    selectedDevice: '',
                    selectedFunction: '',
                    error: true,
                  });
                } else {
                  updateData({
                    node_id: '',
                    deviceType: '',
                    selectedDevice: '',
                    selectedFunction: '',
                    error: true,
                  });
                }
              }}
              options={deviceTypeOptions}
              className="flex-fill"
            />
          )}

          {data?.deviceType && functionList[normalizeTypeKey(data.deviceType)] && (
            <LabeledSelect
              label="Device Action:"
              disabled={readOnly}
              value={data.selectedFunction || ''}
              onChange={(selectedFunction) => updateData({ selectedFunction })}
              options={toValueOptions(functionList[normalizedTypeKey])}
              className="flex-fill"
            />
          )}
        </Toolbar>

        {data.selectedFunction && deviceFunctions[data.selectedFunction]?.component}
      </div>
    );
  }

  //Group
  const groupLocked =
    readOnly && !!data?.group_id && !deviceGroups.some((g) => g.id === data?.group_id);

  return (
    <div className="d-flex flex-column w-100">
      <Toolbar>
        <LabeledSelect
          label="Device Group:"
          locked={groupLocked}
          disabled={readOnly}
          value={group?.name || ''}
          onChange={(groupName) => {
            setSystemType('RescAlert Devices');
            const foundGroup = deviceGroups.find((g) => g.name === groupName);
            if (!foundGroup) return;

            setGroup(foundGroup);

            updateData({
              deviceType: foundGroup.type,
              group_id: foundGroup.id,
              selectedFunction: '',
              status: 0,
              error: true,
            });
          }}
          options={deviceGroups.map((g) => ({ value: g.name, label: g.name }))}
          className="flex-fill"
        />

        {group && getGroupFunctions(group).length > 0 && (
          <LabeledSelect
            label="Group Action:"
            disabled={readOnly}
            value={data.selectedFunction || ''}
            onChange={(selectedFunction) => updateData({ selectedFunction })}
            options={toValueOptions(getGroupFunctions(group))}
            className="flex-fill"
          />
        )}
      </Toolbar>

      {data.selectedFunction && deviceFunctions[data.selectedFunction]?.component}
    </div>
  );
};

export default DeviceAction;
